import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { curatorQueries } from '../../query-factory';
import { isSeedingItemUnviewed, type SeedingUnviewedWatermark } from './seedingUnviewedWatermark';
import { useSeedingWatermark } from './useSeedingWatermark';

const STORAGE_KEY = 'curator:collab:my-recruitment:last-viewed-at';

/**
 * 내가 신청한(모집형) 캠페인 blue dot — 마지막 진입 이후 신규 신청 건 존재 시 노출.
 * 제안형 `useBrandsProposalUnviewed` 와 동일 watermark(id+createdAt) 판정. (SID-4312 / QAT-39429)
 */
export const useMyRecruitmentUnviewed = () => {
  // mount + 재가시마다 watermark 재독 — 목록 웹뷰에서 확인 후 me 페이지 dot 잔존 방지. (CMQA-7180 동류)
  const { mounted, seen, setSeen } = useSeedingWatermark(STORAGE_KEY);

  const { data } = useQuery({
    ...curatorQueries.campaigns.seedingMeRecruitmentLatestPending(),
    enabled: mounted,
    staleTime: 60_000,
  });

  const latest = data?.list?.[0] ?? null;

  // 신청 0개면 진입 이력과 무관하게 미노출. (제안형 SID-4120 '0개 미노출' 규칙과 동일)
  // createdAt 동형 비교 watermark 로 '공백+KST' vs toISOString 이종 비교 결함 제거. (QAT-39429)
  const hasUnviewed = mounted && isSeedingItemUnviewed(latest, seen);

  const markAsViewed = useCallback(() => {
    if (latest == null) return; // PENDING 0건이면 watermark 를 덮지 않는다.
    const next: SeedingUnviewedWatermark = {
      id: latest.participationId,
      createdAt: latest.createdAt,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {}
    setSeen(next);
  }, [latest, setSeen]);

  return { hasUnviewed, markAsViewed };
};
